import { Component, DestroyRef, inject, Input, OnChanges, OnInit } from '@angular/core';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { filter, interval, Observable, Subscription, take } from 'rxjs';
import { AuditReportDto } from '../../common/interfaces/audit-report-dto';
import { AuditReportService } from '../../common/services/audit-report.service';
import { FormatDateArrayPipe } from '../../common/pipes/format-date-array/format-date-array.pipe';
import { NgIf, NgSwitch, NgSwitchCase, TitleCasePipe } from '@angular/common';
import { LazyLoadEvent, PrimeTemplate } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { PaginationComponent } from '../pagination/pagination.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { EnterpriseLabels } from '../../common/interfaces/enterprise-labels';
import { ToastrService } from 'ngx-toastr';
import { AdminAuditEventCategory } from '../../common/enums/admin-audit-event-category.enum';
import { AdminAuditEventType } from '../../common/enums/admin-audit-event-type.enum';
import { AuditReportStatus } from '../../common/enums/audit-report-status';
import { PaginatedSearchFilter } from '../../common/interfaces/paginated-search-filter';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { envToken } from '../../common/tokens/env-token';

@Component({
    selector: 'lib-audit-report-table',
    imports: [
        EmptyStateComponent,
        FormatDateArrayPipe,
        NgIf,
        PaginationComponent,
        PrimeTemplate,
        TableModule,
        FaIconComponent,
        NgSwitchCase,
        NgSwitch,
        TitleCasePipe
    ],
    templateUrl: './audit-report-table.component.html',
    styleUrl: './audit-report-table.component.scss'
})
export class AuditReportTableComponent implements OnInit, OnChanges {
    private readonly auditReportService = inject(AuditReportService);
    private readonly toastrService = inject(ToastrService);
    private env = inject(envToken);

    private SOURCE_SYSTEM_ENTERPRISE_ADMIN = 'ENTERPRISE_ADMIN';
    private pollSubscription?: Subscription;

    private readonly destroyRef = inject(DestroyRef);

    @Input() enterpriseId = 0;
    @Input() enterpriseLabels!: EnterpriseLabels;
    @Input() reloadDataObs: Observable<boolean> = new Observable<boolean>();

    auditReports: AuditReportDto[] = [];
    currentPage = 1;
    itemsPerPage = 10;
    totalItems = 0;
    sortBy = 'creationAudit.dateCreated';
    sortOrder = 'DESC';
    isLoading = false;
    failureMessage = 'Report could not be generated, please try again';
    emptyFileMessage = 'Report was not created because there were no records matching the filter criteria';
    sourceSystem = this.SOURCE_SYSTEM_ENTERPRISE_ADMIN;

    ngOnInit(): void {
        this.sourceSystem = this.env.sourceSystem;
        this.reloadDataObs
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.loadReports();
            });
    }

    ngOnChanges(): void {
        if (this.enterpriseId > 0) {
            this.loadReports();
        }
    }

    loadReports(): void {
        this.isLoading = true;
        this.auditReportService.getByEnterpriseId(this.sourceSystem, this.enterpriseId, this.getPaginatedSearchFilter())
            .subscribe({
                next: (data) => {
                    this.auditReports = data.content
                        .map(child => ({
                            ...child,
                            eventCategory: this.applyEventCategoryLabel(child) ?? child.eventCategory ?? 'All',
                            eventName: this.applyEventNameLabel(child) ?? child.eventName ?? 'Any'
                        }));
                    this.totalItems = data.totalElements;
                    if (data.content.length === 0) {
                        this.currentPage = 1;
                    }
                    this.isLoading = false;
                    this.updatePolling();
                },
                error: () => {
                    this.toastrService.error('Failed to retrieve audit reports.', 'Error');
                    this.isLoading = false;
                }
            });
    }

    customSort(event: LazyLoadEvent): void {
        this.sortBy = event.sortField ? event.sortField : 'creationAudit.dateCreated';
        this.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
        this.loadReports();
    }

    onSelectItemsPerPage(itemsPerPage: number): void {
        this.itemsPerPage = itemsPerPage;
    }

    onChangeCurrentPage(currentPage: number): void {
        this.currentPage = currentPage;
        this.loadReports();
    }

    getPaginatedSearchFilter(): PaginatedSearchFilter {
        return {
            page: this.currentPage - 1,
            itemsPerPage: this.itemsPerPage,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder
        } as PaginatedSearchFilter;
    }

    openCsv(fileName: string): void {
        this.auditReportService.getCsv(this.sourceSystem, fileName)
            .pipe(take(1))
            .subscribe({
                next: (s3Url) => {
                    const link = document.createElement('a');
                    link.href = s3Url;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                },
                error:
                        (err) => console.error('CSV download failed:', err)
            }
            );
    }

    private updatePolling(): void {
        const hasActiveReports = this.auditReports.some(r =>
            r.status === AuditReportStatus.PENDING || r.status === AuditReportStatus.PROCESSING
        );
        if (hasActiveReports && !this.pollSubscription) {
            this.pollSubscription = interval(this.env.auditReportPollIntervalMs ?? 30000)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => !this.isLoading)
                )
                .subscribe(() => this.loadReports());
        } else if (!hasActiveReports && this.pollSubscription) {
            this.pollSubscription.unsubscribe();
            this.pollSubscription = undefined;
        }
    }

    isOrgAdminApp(): boolean {
        return this.sourceSystem === this.SOURCE_SYSTEM_ENTERPRISE_ADMIN;
    }

    private applyEventNameLabel = (child: AuditReportDto) => {
        return Object.entries(AdminAuditEventType as Record<string, string>).find(([key]) => key === child.eventName)?.[ 1 ]
            .replace('Universal ID', this.enterpriseLabels.universalIdLabel);
    };

    private applyEventCategoryLabel = (child: AuditReportDto) => {
        return Object.entries(AdminAuditEventCategory as Record<string, string>).find(([key]) => key === child.eventCategory)?.[ 1 ]
            .replace('Universal ID', this.enterpriseLabels.universalIdLabel)
            .replace('EMA Account', this.enterpriseLabels.accountLabel);
    };

    protected readonly faCircleExclamation = faCircleExclamation;
    protected readonly AuditReportStatus = AuditReportStatus;
}
